import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ColumnDef, SortingState } from "@tanstack/react-table";

import { DataTable } from "@/_v2/components/composites/data-table/data-table";
import { Badge } from "@/_v2/components/ui/badge";
import { Input } from "@/_v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/_v2/components/ui/select";

import { FLAG_CATEGORIES } from "../flag-catalog";
import { FlagInsight, FlagStatus, FLAG_STATUS_LABELS } from "../lib";

import { CategoryBadge } from "./category-badge";
import { FlagStatusBadge } from "./flag-status-badge";

// Sorting is applied manually (sortInsights below) since the DataTable
// composite runs with manualSorting, but each sortable column still needs an
// accessor — TanStack's getCanSort() returns false without one and the header
// sort buttons never render.
const columns: ColumnDef<FlagInsight>[] = [
  {
    id: "name",
    accessorKey: "displayName",
    header: "Feature Flag",
    enableSorting: true,
    cell: ({ row }) => (
      <Link
        to={`/v2/feature-flag-insights/${row.original.name}`}
        className="group inline-block"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="font-medium group-hover:underline">
          {row.original.displayName}
        </div>
        <div className="font-mono text-xs text-muted-foreground">
          {row.original.name}
        </div>
      </Link>
    ),
  },
  {
    id: "description",
    header: "Description",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.documented ? (
        <p className="max-w-xl text-sm text-muted-foreground">
          {row.original.description}
        </p>
      ) : (
        <Badge variant="outline">Undocumented</Badge>
      ),
  },
  {
    id: "category",
    accessorFn: (insight) => insight.category ?? "",
    header: "Category",
    enableSorting: true,
    cell: ({ row }) =>
      row.original.category ? (
        <CategoryBadge category={row.original.category} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: "status",
    accessorKey: "status",
    header: "Status",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="space-y-1">
        <FlagStatusBadge status={row.original.status} />
        {row.original.hasExceptions ? (
          <div className="text-xs text-muted-foreground">
            {row.original.disabledOrganizations.length +
              row.original.disabledGroups.length +
              row.original.disabledUsers.length}{" "}
            explicit disables
          </div>
        ) : null}
      </div>
    ),
  },
  {
    id: "orgs",
    accessorFn: (insight) => insight.enabledOrganizations.length,
    header: "Orgs",
    enableSorting: true,
    cell: ({ row }) => row.original.enabledOrganizations.length,
  },
  {
    id: "groups",
    accessorFn: (insight) => insight.enabledGroups.length,
    header: "Groups",
    enableSorting: true,
    cell: ({ row }) => row.original.enabledGroups.length,
  },
  {
    id: "users",
    accessorFn: (insight) => insight.enabledUsers.length,
    header: "Users",
    enableSorting: true,
    cell: ({ row }) => row.original.enabledUsers.length,
  },
];

const STATUS_ORDER: Record<FlagStatus, number> = {
  global: 2,
  partial: 1,
  off: 0,
};

function sortInsights(
  insights: FlagInsight[],
  sorting: SortingState
): FlagInsight[] {
  if (!sorting.length) {
    return insights;
  }

  const { id, desc } = sorting[0];
  const sorted = [...insights].sort((a, b) => {
    switch (id) {
      case "name":
        return a.displayName.localeCompare(b.displayName);
      case "category":
        return (a.category ?? "").localeCompare(b.category ?? "");
      case "status":
        return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      case "orgs":
        return a.enabledOrganizations.length - b.enabledOrganizations.length;
      case "groups":
        return a.enabledGroups.length - b.enabledGroups.length;
      case "users":
        return a.enabledUsers.length - b.enabledUsers.length;
      default:
        return 0;
    }
  });

  return desc ? sorted.reverse() : sorted;
}

interface FlagInsightsTableProps {
  insights: FlagInsight[];
  loading?: boolean;
}

export const FlagInsightsTable = ({
  insights,
  loading,
}: FlagInsightsTableProps) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | FlagStatus>("all");
  const [category, setCategory] = useState<string>("all");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    const matches = insights.filter((insight) => {
      if (status !== "all" && insight.status !== status) {
        return false;
      }
      if (category === "uncategorized" && insight.category) {
        return false;
      }
      if (
        category !== "all" &&
        category !== "uncategorized" &&
        insight.category !== category
      ) {
        return false;
      }
      if (
        term &&
        !insight.name.includes(term) &&
        !insight.displayName.toLowerCase().includes(term) &&
        !(insight.description || "").toLowerCase().includes(term)
      ) {
        return false;
      }
      return true;
    });

    return sortInsights(matches, sorting);
  }, [insights, search, status, category, sorting]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by name or description…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-xs"
        />
        <Select
          value={status}
          onValueChange={(value) => setStatus(value as "all" | FlagStatus)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(Object.keys(FLAG_STATUS_LABELS) as FlagStatus[]).map((value) => (
              <SelectItem key={value} value={value}>
                {FLAG_STATUS_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {FLAG_CATEGORIES.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
            <SelectItem value="uncategorized">Uncategorized</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto text-sm text-muted-foreground">
          {filtered.length} of {insights.length} flags
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        rowCount={filtered.length}
        loading={loading}
        sorting={[sorting, setSorting]}
        getRowHref={(row) => `/v2/feature-flag-insights/${row.original.name}`}
      />
    </div>
  );
};
